import type { CurrencyEnum } from '../../../../../generated/enums.ts';
import {ScannedFields} from '../../../../../views/DocumentDrawer/types/Document';
import {BackendAPITypes} from '../../../../../generated/blueprint';
import {BackendApi} from '../../../../../api/api';
import {canCreditCardStatementsMatchFilters, mergeBillsAndCreditCardStatements} from './partnerDocumentsMerge';

import {
    DocumentMatchingExclusionReasonEnum,
} from '../../../../../types/enums';
import type {DateFilterType, DocumentCategory, DocumentDirection, FilterTags, OrderDirectionEnum} from '../../../../../generated/enums.ts';
import type {MonetaryOrigin} from '../../../../../components/general/ForeignAmountLabel.tsx';

export interface Business {
    id: string;
    name: string;
}

export interface FilterOption {
    id: string;
    name: string;
    business_id?: string;
}

export interface Tag {
    name: string;
    id: string;
}

export interface DocumentFilters {
    dateFrom?: string;
    dateTo?: string;
    dateType?: DateFilterType;
    customerId?: string | null;
    relations?: string[];
    documentDirection?: DocumentDirection | "all";
    documentCategories?: DocumentCategory[];
    tags?: string[];
    tagsMode?: FilterTags;
}

export interface FilterOptions {
    relations: FilterOption[];
}

export enum PartnerDocumentSource {
    BILL = 'bill',
    CREDIT_CARD_STATEMENT = 'credit_card_statement',
}

interface PartnerDocumentBase {
    id: string;
    file_name: string;
    document_direction: DocumentDirection | null;
    document_category: DocumentCategory | null;
    business_id: string;
    business_name?: string;
    created_at: string;
}

export interface PartnerBill extends PartnerDocumentBase {
    source: PartnerDocumentSource.BILL;
    bill_id: string;
    reconciliation_id: string | null;
    is_vat_ignored: boolean;
    scanned_fields: ScannedFields;
    relation_name: string;
    total_amount_payable: number;
    currency: CurrencyEnum;
    is_matched: boolean;
    needs_write_off: boolean;
    matching_exclusion_reason: DocumentMatchingExclusionReasonEnum | null;
    monetary_origin?: MonetaryOrigin | null;
    fixed_asset_id: string | null;
    fixed_asset_name: string | null;
}

export interface PartnerCreditCardStatement extends PartnerDocumentBase {
    source: PartnerDocumentSource.CREDIT_CARD_STATEMENT;
    is_credit_card_statement_settled: boolean;
}

export type PartnerBillOrCreditCardStatement = PartnerBill | PartnerCreditCardStatement;

export function isCreditCardStatement(document: PartnerBillOrCreditCardStatement): document is PartnerCreditCardStatement {
    return document.source === PartnerDocumentSource.CREDIT_CARD_STATEMENT;
}

const SETTLED_STATEMENT_CANNOT_BE_DELETED = 'Kan niet verwijderd worden: afschrift is verrekend';
const MATCHED_BILL_CANNOT_BE_DELETED = 'Kan niet verwijderd worden: document is gematcht';

export function getDeleteBlockedReason(doc: PartnerBillOrCreditCardStatement): string | null {
    if (isCreditCardStatement(doc)) {
        return doc.is_credit_card_statement_settled ? SETTLED_STATEMENT_CANNOT_BE_DELETED : null;
    }

    return doc.is_matched ? MATCHED_BILL_CANNOT_BE_DELETED : null;
}

// The backend types these as plain str/dict, so the blueprint widens them. Until the DTOs carry
// their enums, this is the one place that narrows them back.
type BackendBill = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerListClientBillsGetResponseBody['bills'][number];
type BackendCreditCardStatement =
    BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerListCreditCardStatementsGetResponseBody['credit_card_statements'][number];

function toPartnerBill(bill: BackendBill): PartnerBill {
    return {
        ...bill,
        source: PartnerDocumentSource.BILL,
        document_direction: bill.document_direction as DocumentDirection,
        document_category: bill.document_category as DocumentCategory,
        matching_exclusion_reason: bill.matching_exclusion_reason as DocumentMatchingExclusionReasonEnum | null,
        scanned_fields: bill.scanned_fields as ScannedFields,
    };
}

function toPartnerCreditCardStatement(statement: BackendCreditCardStatement): PartnerCreditCardStatement {
    return {
        ...statement,
        source: PartnerDocumentSource.CREDIT_CARD_STATEMENT,
        document_direction: statement.document_direction as DocumentDirection,
        document_category: statement.document_category as DocumentCategory,
    };
}

export interface DocumentsResponse {
    documents: PartnerBillOrCreditCardStatement[];
    filter_options: {relations: FilterOption[]};
}

export interface SortingState {
    orderby: string;
    order: OrderDirectionEnum;
}

export interface DocumentTableProps {
    documents: PartnerBillOrCreditCardStatement[];
    sortingState: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    filterOptions: FilterOptions;
    filters: DocumentFilters;
}

export interface DocumentFilterBarProps {
    availableRelations: FilterOption[];
    availableTags: Tag[];
    displayValue?: (value: string) => string;
}

interface PartnerDocumentsQuery {
    date_from: string | null;
    date_to: string | null;
    business_ids: string | null;
    relations: string | null;
    tags: string | null;
    tags_mode: FilterTags;
    direction: DocumentDirection | null;
    document_categories: string | null;
    date_type: DateFilterType;
    orderby: string;
    order: string;
}

interface CreditCardStatementsQuery {
    date_from: string | null;
    date_to: string | null;
    business_ids: string | null;
    orderby: string;
    order: string;
}

function billDateType(filters: DocumentFilters): DateFilterType {
    if (filters.documentDirection === 'other') {
        return 'created';
    }
    return filters.dateType ?? 'invoice';
}

function buildQuery(
    filters: DocumentFilters,
    sortingState: SortingState,
): PartnerDocumentsQuery {
    return {
        date_from: filters.dateFrom || null,
        date_to: filters.dateTo || null,
        business_ids: filters.customerId || null,
        relations: filters.relations?.length ? JSON.stringify(filters.relations) : null,
        tags: filters.tags?.length ? JSON.stringify(filters.tags) : null,
        tags_mode: filters.tagsMode || 'is',
        direction:
            filters.documentDirection && filters.documentDirection !== 'all'
                ? filters.documentDirection
                : null,
        document_categories: filters.documentCategories?.length ? filters.documentCategories.join(',') : null,
        date_type: billDateType(filters),
        orderby: sortingState.orderby,
        order: sortingState.order,
    };
}

function buildCreditCardStatementsQuery(query: PartnerDocumentsQuery): CreditCardStatementsQuery {
    return {
        date_from: query.date_from,
        date_to: query.date_to,
        business_ids: query.business_ids,
        orderby: query.orderby,
        order: query.order,
    };
}

export const PartnerDocumentsRepository = {
    async fetchBillDocuments(
        api: BackendApi,
        query: PartnerDocumentsQuery,
    ): Promise<DocumentsResponse> {
        const response = await api.Partner__listClientBills(
            query.date_from,
            query.date_to,
            query.date_type,
            query.business_ids,
            query.relations,
            query.tags,
            query.tags_mode,
            query.direction,
            query.document_categories,
            query.orderby,
            query.order,
        );
        return {
            documents: response.bills.map(toPartnerBill),
            filter_options: {relations: response.filter_options.relations},
        };
    },

    async fetchCreditCardStatements(
        api: BackendApi,
        query: CreditCardStatementsQuery,
    ): Promise<PartnerCreditCardStatement[]> {
        const response = await api.Partner__listCreditCardStatements(
            query.date_from,
            query.date_to,
            query.business_ids,
            query.orderby,
            query.order,
        );
        return response.credit_card_statements.map(toPartnerCreditCardStatement);
    },

    async fetchAllDocuments(
        api: BackendApi,
        filters: DocumentFilters,
        sortingState: SortingState,
    ): Promise<DocumentsResponse> {
        const query = buildQuery(filters, sortingState);

        if (!canCreditCardStatementsMatchFilters(filters)) {
            return await this.fetchBillDocuments(api, query);
        }

        const [bills, creditCardStatements] = await Promise.all([
            this.fetchBillDocuments(api, query),
            this.fetchCreditCardStatements(api, buildCreditCardStatementsQuery(query)),
        ]);

        return mergeBillsAndCreditCardStatements(bills, creditCardStatements);
    },
};
